<template>
  <Teleport to="body">
    <div class="cf-outline">
      <Transition name="cf-outline-panel">
        <div v-if="open" class="cf-outline__panel" data-cy="cf-outline-panel">
          <div class="cf-outline__title">Jump to</div>
          <ul class="cf-outline__sections">
            <li
              v-for="section in items"
              :key="section.key"
              class="cf-outline__section"
            >
              <button
                type="button"
                class="cf-outline__section-btn"
                :class="{
                  'cf-outline__section-btn--current': section.isCurrent
                }"
                data-cy="cf-outline-section"
                @click="emitJump('jump-section', section.key)"
              >
                <span
                  class="cf-outline__dot"
                  :class="{ 'cf-outline__dot--on': section.isCurrent }"
                ></span>
                <span
                  class="cf-outline__name"
                  :class="{ 'cf-outline__name--empty': !section.name }"
                  >{{ section.name || "Untitled section" }}</span
                >
                <span class="cf-outline__count">{{ section.count }}</span>
              </button>

              <ul v-if="section.questions.length" class="cf-outline__questions">
                <li v-for="q in section.questions" :key="q.key">
                  <button
                    type="button"
                    class="cf-outline__question"
                    :class="{
                      'cf-outline__question--active': q.isActive,
                      'cf-outline__question--open': q.isOpen && !q.isActive
                    }"
                    data-cy="cf-outline-question"
                    @click="emitJump('jump-question', q.key)"
                  >
                    <span class="cf-outline__tick"></span>
                    <span
                      class="cf-outline__name"
                      :class="{ 'cf-outline__name--empty': !q.title }"
                      >{{ q.title || "Untitled question" }}</span
                    >
                  </button>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </Transition>
      <button
        type="button"
        class="cf-outline__toggle"
        :aria-expanded="open"
        data-cy="cf-outline-toggle"
        @click="open = !open"
      >
        <i class="fas" :class="open ? 'fa-chevron-right' : 'fa-list-ul'"></i>
        <span class="cf-outline__toggle-label">Outline</span>
      </button>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import type { BuilderSection } from "./customFormFields";

const props = withDefaults(
  defineProps<{
    sections: BuilderSection[];
    activeQuestionKey?: string | null;
    openQuestionKeys?: string[];
  }>(),
  { activeQuestionKey: null, openQuestionKeys: () => [] }
);
const emit = defineEmits<{
  "jump-section": [key: string];
  "jump-question": [key: string];
}>();

const open = ref(false);

// A full section → question tree. The section holding your cursor is "current";
// each question is marked active (the one you're editing) or open (expanded), so
// the outline reflects exactly what's on the page and lets you jump into any row.
const items = computed(() =>
  props.sections.map((section) => ({
    key: section._key,
    name: section.name,
    count: section.questions.length,
    isCurrent: section.questions.some(
      (q) => q._key === props.activeQuestionKey
    ),
    questions: section.questions.map((q) => ({
      key: q._key,
      title: q.title,
      isActive: q._key === props.activeQuestionKey,
      isOpen: props.openQuestionKeys.includes(q._key)
    }))
  }))
);

function emitJump(event: "jump-section" | "jump-question", key: string) {
  open.value = false;
  emit(event, key);
}
</script>

<style scoped>
/* Docked to the middle of the right edge — a drawer handle that stays reachable
   while scrolling a long form and never collides with the bottom-right Done. */
.cf-outline {
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 40;
  display: flex;
  align-items: center;
}

.cf-outline__toggle {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 0.5rem;
  border: 1px solid rgb(226 232 240);
  border-right: none;
  border-radius: 0.75rem 0 0 0.75rem;
  background: #ffffff;
  color: #1d4ed8;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: -8px 0 20px -8px rgba(15, 23, 42, 0.22);
  transition: transform 150ms ease, box-shadow 150ms ease;
}
.cf-outline__toggle:hover {
  transform: translateX(-2px);
  box-shadow: -12px 0 24px -8px rgba(15, 23, 42, 0.3);
}
.cf-outline__toggle:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}
.cf-outline__toggle-label {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  letter-spacing: 0.03em;
}

.cf-outline__panel {
  width: 19rem;
  max-height: 70vh;
  overflow-y: auto;
  margin-right: 0.5rem;
  padding: 0.5rem;
  border-radius: 0.75rem;
  border: 1px solid rgb(226 232 240);
  background: #ffffff;
  box-shadow: 0 20px 40px -12px rgba(15, 23, 42, 0.3),
    0 8px 16px -8px rgba(15, 23, 42, 0.16);
}
.cf-outline__title {
  padding: 0.25rem 0.625rem 0.5rem;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
}
.cf-outline__sections {
  list-style: none;
  margin: 0;
  padding: 0;
}
.cf-outline__section {
  margin-bottom: 0.25rem;
}

.cf-outline__section-btn,
.cf-outline__question {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 0.5rem;
  border-radius: 0.5rem;
  text-align: left;
  cursor: pointer;
  transition: background-color 120ms ease;
}
.cf-outline__section-btn {
  padding: 0.4rem 0.625rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1e293b;
}
.cf-outline__section-btn:hover {
  background: rgb(248 250 252);
}
.cf-outline__section-btn--current {
  color: #1d4ed8;
}

.cf-outline__questions {
  list-style: none;
  margin: 0 0 0.25rem;
  padding: 0;
  /* Rail down the section's questions so the nesting reads at a glance. */
  border-left: 1px solid rgb(226 232 240);
  margin-left: 0.9rem;
}
.cf-outline__question {
  padding: 0.3rem 0.625rem;
  font-size: 0.8125rem;
  color: #64748b;
}
.cf-outline__question:hover {
  background: rgb(248 250 252);
}
.cf-outline__question--open {
  color: #334155;
  background: rgb(248 250 252);
}
.cf-outline__question--active {
  color: #1d4ed8;
  font-weight: 600;
  background: rgb(239 246 255);
}

.cf-outline__dot {
  flex-shrink: 0;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  background: transparent;
  box-shadow: inset 0 0 0 1.5px rgb(203 213 225);
}
.cf-outline__dot--on {
  background: #2563eb;
  box-shadow: none;
}
.cf-outline__tick {
  flex-shrink: 0;
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 9999px;
  background: rgb(203 213 225);
}
.cf-outline__question--open .cf-outline__tick {
  background: #60a5fa;
}
.cf-outline__question--active .cf-outline__tick {
  background: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.18);
}

.cf-outline__name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cf-outline__name--empty {
  font-style: italic;
  color: #94a3b8;
}
.cf-outline__count {
  flex-shrink: 0;
  font-size: 0.6875rem;
  font-variant-numeric: tabular-nums;
  color: #94a3b8;
}

.cf-outline-panel-enter-active,
.cf-outline-panel-leave-active {
  transition: opacity 160ms ease, transform 160ms ease;
  transform-origin: right center;
}
.cf-outline-panel-enter-from,
.cf-outline-panel-leave-to {
  opacity: 0;
  transform: translateX(12px) scale(0.98);
}

@media (prefers-reduced-motion: reduce) {
  .cf-outline__toggle,
  .cf-outline-panel-enter-active,
  .cf-outline-panel-leave-active {
    transition: none;
  }
}
</style>
